import { useCallback, useEffect, useRef } from 'react'

import { Skeleton, Table, Tooltip } from '@mantine/core'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

import { useQuery } from '@redwoodjs/web'

// GraphQL `DateTime` fields are UTC-midnight ISO strings — without this
// plugin, west-of-UTC users see the prior month's label. Matches the
// convention in TrendChart.tsx.
dayjs.extend(utc)

import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_RETENTION_COHORTS } from 'src/graphql/retentionMetrics.graphql'
import { cn } from 'src/utils'

import AnalyticsCard from '../../components/AnalyticsCard'

import type { StatusLogFiltersInput } from 'types/graphql'

const INFO_TEXT = [
  'Each row is a group of hires from one month, showing how many were still active 30, 60, and 90 days later.',
  'Reading down a column lets you spot whether retention is trending up or down month over month — useful for connecting a change (new pay package, new orientation flow) to its effect.',
  'A dash ("—") means not enough time has passed yet for everyone in that group to reach that milestone, so we don\'t show a misleading partial number.',
].join('\n\n')

// Backend returns retentionPct on a 0–100 scale (e.g. 73.0 for 73%).
const GOOD_THRESHOLD = 70
const POOR_THRESHOLD = 45
const SMALL_COHORT_THRESHOLD = 5

export interface CohortRetentionRow {
  hireMonth: string
  cohortSize: number
  retained30?: number | null
  retained60?: number | null
  retained90?: number | null
  retentionPct30?: number | null
  retentionPct60?: number | null
  retentionPct90?: number | null
  hasFullWindow30: boolean
  hasFullWindow60: boolean
  hasFullWindow90: boolean
}

const toneForPct = (
  pct: number | null | undefined,
  cohortSize?: number | null
): string => {
  if (pct == null) return 'text-doubleNickel-gray-400'
  if (cohortSize != null && cohortSize < SMALL_COHORT_THRESHOLD) {
    return 'text-doubleNickel-gray-800'
  }
  if (pct >= GOOD_THRESHOLD) return 'font-semibold text-doubleNickel-success-700'
  if (pct < POOR_THRESHOLD) return 'text-doubleNickel-error-600'
  return 'text-doubleNickel-gray-800'
}

const formatPct = (pct: number | null | undefined): string => {
  if (pct == null) return '—'
  return `${Math.round(pct)}%`
}

/**
 * Format the hire-month bucket. Backend returns a UTC-midnight ISO string for
 * the first of the month — we render it as "Mar 2026" using dayjs (the
 * codebase-wide date util on the analytics surface, see TrendChart).
 */
const formatHireMonth = (hireMonth: string): string =>
  dayjs.utc(hireMonth).format('MMM YYYY')

interface CohortPctCellProps {
  pct: number | null | undefined
  hasFullWindow: boolean
  windowDays: 30 | 60 | 90
}

const CohortPctCell = ({ pct, hasFullWindow, windowDays }: CohortPctCellProps) => {
  const showDash = !hasFullWindow || pct == null
  if (showDash) {
    return (
      <Tooltip
        label={`${windowDays}-day window not fully elapsed yet.`}
        withArrow
        position="top"
      >
        <span className="cursor-help text-doubleNickel-gray-400">—</span>
      </Tooltip>
    )
  }
  return <span>{formatPct(pct)}</span>
}

export interface CohortRetentionTableViewProps {
  rows: ReadonlyArray<CohortRetentionRow>
  loading?: boolean
}

export const CohortRetentionTableView = ({
  rows,
  loading = false,
}: CohortRetentionTableViewProps) => {
  if (loading) {
    return (
      <Table verticalSpacing="xs" horizontalSpacing="sm" fz="xs">
        <Table.Tbody>
          {Array.from({ length: 5 }).map((_, i) => (
            <Table.Tr key={i}>
              <Table.Td>
                <Skeleton height={12} width="60%" />
              </Table.Td>
              <Table.Td>
                <Skeleton height={12} width={36} />
              </Table.Td>
              <Table.Td>
                <Skeleton height={12} width={36} />
              </Table.Td>
              <Table.Td>
                <Skeleton height={12} width={36} />
              </Table.Td>
              <Table.Td>
                <Skeleton height={12} width={36} />
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    )
  }

  if (rows.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-xs text-doubleNickel-gray-500">
        No cohort data for this period.
      </div>
    )
  }

  return (
    <div className="max-h-[360px] overflow-y-auto">
      <Table
        verticalSpacing="xs"
        horizontalSpacing="sm"
        fz="xs"
        stickyHeader
        stickyHeaderOffset={0}
      >
        <Table.Thead className="bg-white">
          <Table.Tr>
            <Table.Th className="text-doubleNickel-gray-600">Hire Month</Table.Th>
            <Table.Th className="text-right text-doubleNickel-gray-600">
              Cohort Size
            </Table.Th>
            <Table.Th className="text-right text-doubleNickel-gray-600">
              30-Day
            </Table.Th>
            <Table.Th className="text-right text-doubleNickel-gray-600">
              60-Day
            </Table.Th>
            <Table.Th className="text-right text-doubleNickel-gray-600">
              90-Day
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
      <Table.Tbody>
        {rows.map((row) => {
          const show30 = row.hasFullWindow30 && row.retentionPct30 != null
          const show60 = row.hasFullWindow60 && row.retentionPct60 != null
          const show90 = row.hasFullWindow90 && row.retentionPct90 != null
          return (
            <Table.Tr key={row.hireMonth} data-testid={`cohort-row-${row.hireMonth}`}>
              <Table.Td className="font-medium text-doubleNickel-gray-800">
                {formatHireMonth(row.hireMonth)}
              </Table.Td>
              <Table.Td className="text-right font-mono tabular-nums text-doubleNickel-gray-700">
                {row.cohortSize.toLocaleString()}
              </Table.Td>
              <Table.Td
                className={cn(
                  'text-right font-mono tabular-nums',
                  show30
                    ? toneForPct(row.retentionPct30, row.cohortSize)
                    : 'text-doubleNickel-gray-400'
                )}
              >
                <CohortPctCell
                  pct={row.retentionPct30}
                  hasFullWindow={row.hasFullWindow30}
                  windowDays={30}
                />
              </Table.Td>
              <Table.Td
                className={cn(
                  'text-right font-mono tabular-nums',
                  show60
                    ? toneForPct(row.retentionPct60, row.cohortSize)
                    : 'text-doubleNickel-gray-400'
                )}
              >
                <CohortPctCell
                  pct={row.retentionPct60}
                  hasFullWindow={row.hasFullWindow60}
                  windowDays={60}
                />
              </Table.Td>
              <Table.Td
                className={cn(
                  'text-right font-mono tabular-nums',
                  show90
                    ? toneForPct(row.retentionPct90, row.cohortSize)
                    : 'text-doubleNickel-gray-400'
                )}
              >
                <CohortPctCell
                  pct={row.retentionPct90}
                  hasFullWindow={row.hasFullWindow90}
                  windowDays={90}
                />
              </Table.Td>
            </Table.Tr>
          )
        })}
      </Table.Tbody>
      </Table>
    </div>
  )
}

export interface CohortRetentionTableProps {
  filters: StatusLogFiltersInput
  /**
   * Bumped by the page-level Refresh button — when this value changes (after
   * the initial mount), the card calls its own `refetch()` to re-pull data
   * with the same filters.
   */
  refreshKey: number
}

const CohortRetentionTable = ({
  filters,
  refreshKey,
}: CohortRetentionTableProps) => {
  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  const { data, loading, refetch } = useQuery(GET_RETENTION_COHORTS, {
    variables: { filters },
    onError,
    notifyOnNetworkStatusChange: true,
  })

  const isFirstRefreshKey = useRef(true)
  useEffect(() => {
    if (isFirstRefreshKey.current) {
      isFirstRefreshKey.current = false
      return
    }
    refetch()
  }, [refreshKey, refetch])

  const rows = (data?.retentionCohorts?.rows ??
    []) as ReadonlyArray<CohortRetentionRow>

  return (
    <AnalyticsCard title="Cohort Retention" info={INFO_TEXT}>
      <CohortRetentionTableView rows={rows} loading={loading && !data} />
    </AnalyticsCard>
  )
}

export default CohortRetentionTable
